/**
 * Floating score numbers.
 *
 * A pooled set of `Text` objects that rise out of a pile, coloured by outcome
 * and rendered at device-pixel resolution so a "+600" stays crisp on a 4K panel
 * while the whole design-resolution stage is being scaled to fit.
 *
 * The hard problem here is stacking. One play can legitimately score a wild, a
 * 21, a five-card *and* a streak, and a fast player can land the next play on
 * the same pile before the previous numbers have cleared. Overlapping them
 * turns four readable numbers into one unreadable smear, so every popup is
 * placed by {@link ScorePopupLayer.show} against the popups already in flight:
 * a colliding popup is pushed *down* the column and nudged sideways, and each
 * entry in a stack is delayed slightly behind the one before it, so the group
 * cascades upward and reads as a list instead of a pile.
 *
 * Motion is hand-rolled rather than tweened. There is one animation shape here,
 * every popup runs it, and driving it from the frame's `dt` keeps the layer
 * allocation-free and independent of any other module's timeline.
 *
 * The drop shadow is not decorative: its angle, blur and alpha come from the
 * table's `LIGHT` and `SHADOW` constants, so the text sits in the same faked
 * lighting as the cards instead of floating in a different world.
 */

import { Container, Text, TextStyle, type FillGradient } from 'pixi.js';

import { textFill } from '@/core/fills';

import { motion, palette, type as typeTokens } from '@/core/tokens';
import { CARD, DESIGN, LIGHT, SHADOW } from '@/pixi/layout';

/** Which outcome a popup is announcing. Drives colour, size and behaviour. */
export type PopupVariant =
  | 'twentyOne'
  | 'fiveCard'
  | 'wild'
  | 'streak'
  | 'bust'
  | 'score'
  | 'grand';

/** One line in a stacked group. */
export interface PopupEntry {
  text: string;
  variant: PopupVariant;
}

export interface PopupRequest extends PopupEntry {
  x: number;
  y: number;
  /** Seconds to wait before the popup appears. */
  delay?: number;
  /** Extra vertical offset applied before collision avoidance. */
  offsetY?: number;
}

export interface ScorePopupOptions {
  /** Maximum simultaneous popups. Default 28 — a full stack plus overlap. */
  capacity?: number;
  /** No overshoot, shorter travel, no sideways drift. Default false. */
  reducedMotion?: boolean;
}

/* ------------------------------------------------------------------ style -- */

/**
 * How high the numbers float above the felt. Feeds the shadow model below, so
 * the popup's shadow is as long and as soft as a lifted card's would be.
 */
const POPUP_LIFT = CARD.height * 0.14;

/** Low enough to be invisible, high enough not to be culled. */
const WARM_ALPHA = 0.002;

/**
 * The direction a shadow falls on this table: from the virtual key light toward
 * the middle of the felt. Derived, never guessed — if `LIGHT` moves, this moves
 * with it and the popups keep agreeing with the cards.
 */
function shadowAngle(): number {
  return Math.atan2(DESIGN.height * 0.5 - LIGHT.y, DESIGN.width * 0.5 - LIGHT.x);
}
const SHADOW_DISTANCE = SHADOW.offsetScale * POPUP_LIFT;
const SHADOW_BLUR = SHADOW.blurBase + SHADOW.blurPerLift * POPUP_LIFT;

/** Display tracking from the type token, as a multiple of the font size. */
const TRACKING = Number.parseFloat(typeTokens.displayTracking) || 0;

interface VariantSpec {
  /** Font size as a fraction of the card height, so type scales with the table. */
  size: number;
  colour: number;
  /** Seconds on screen, excluding the delay. */
  life: number;
  /** Travel distance as a fraction of the card height. */
  rise: number;
  /** Bust sinks instead of rising. */
  sink: boolean;
  /** Overshoot strength on the entrance pop. */
  overshoot: number;
  /** Scale at the end of life, relative to 1. */
  endScale: number;
}

/**
 * Vertical gradient for a popup's glyphs: near-white at the top, the variant's
 * own colour through the middle, deepened at the base.
 *
 * A flat fill is what separates a number that looks placed from one that looks
 * struck. These fire on every play, so it is the highest-frequency piece of
 * type in the game and the cheapest place to buy polish — and it matches the
 * numerals on the streak badge, so the two read as one type system rather than
 * as a callout that happens to sit near some score text.
 *
 * Built per variant and cached alongside the style, never per popup.
 */
function gradientFor(colour: number): FillGradient | number {
  return textFill([
    { offset: 0, color: mixRgb(colour, palette.white, 0.72), alpha: 1 },
    { offset: 0.46, color: colour, alpha: 1 },
    { offset: 1, color: mixRgb(colour, palette.ink, 0.42), alpha: 1 },
  ]);
}

/** Linear blend between two 0xRRGGBB colours. */
function mixRgb(a: number, b: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (Math.round(ar + (br - ar) * k) << 16) |
    (Math.round(ag + (bg - ag) * k) << 8) |
    Math.round(ab + (bb - ab) * k)
  );
}

export const VARIANTS: Record<PopupVariant, VariantSpec> = {
  // Exactly 21. Precise, quick, mint.
  twentyOne: { size: 0.24, colour: palette.mint, life: 1.05, rise: 0.42, sink: false, overshoot: 2.8, endScale: 1.06 },
  // The biggest per-pile payout. Largest of the bonus numbers, and lingers longest.
  fiveCard: { size: 0.27, colour: palette.gold, life: 1.2, rise: 0.46, sink: false, overshoot: 2.6, endScale: 1.08 },
  // The rescue. Violet, and it floats a little further than the rest.
  wild: { size: 0.23, colour: palette.violetLight, life: 1.25, rise: 0.52, sink: false, overshoot: 3.0, endScale: 1.06 },
  // The multiplier callout. Loudest type on the table short of the finale.
  streak: { size: 0.29, colour: palette.gold, life: 1.15, rise: 0.5, sink: false, overshoot: 3.2, endScale: 1.1 },
  // Bust falls, shrinks, and is gone before it can be dwelt on.
  bust: { size: 0.2, colour: palette.rose, life: 0.8, rise: 0.2, sink: true, overshoot: 0.6, endScale: 0.88 },
  // Neutral running-total callout.
  score: { size: 0.2, colour: palette.white, life: 0.95, rise: 0.36, sink: false, overshoot: 2.0, endScale: 1.04 },
  // Perfect game. Centre of the table, and it stays up.
  grand: { size: 0.46, colour: palette.gold, life: 2.1, rise: 0.34, sink: false, overshoot: 2.4, endScale: 1.12 },
};

/* ------------------------------------------------------------- animation -- */

/** Entrance duration. Shares the UI motion vocabulary so popups feel like the HUD. */
const IN_TIME = motion.ui;
/** Fade-out duration at the tail of the life. */
const OUT_TIME = motion.ui * 1.4;
/** Alpha ramp on entry — faster than the scale so nothing appears as a ghost. */
const FADE_IN_TIME = motion.ui * 0.35;

/** Vertical step between stacked popups. Comfortably clear of the tallest bonus glyph. */
const STACK_STEP = CARD.height * 0.26;
/**
 * Room a popup keeps between itself and the edge of the design rect.
 *
 * Half a card: enough that a number reads as sitting *on* the table rather than
 * against its frame, at every rung of type size the variants use.
 */
const POPUP_EDGE = CARD.height * 0.5;
/** Sideways nudge alternated down a stack so a column never lines up dead straight. */
const STACK_NUDGE = CARD.width * 0.13;
/** Delay between successive entries in one stack, so they cascade. */
const STACK_STAGGER = 0.08;

/** Region treated as "occupied" when placing a new popup. */
const COLLIDE_X = CARD.width * 0.6;
const COLLIDE_Y = CARD.height * 0.22;
const MAX_PLACE_ATTEMPTS = 6;

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** Back-out ease: overshoots past 1 and settles. `s` controls how far past. */
function backOut(u: number, s: number): number {
  const p = u - 1;
  return p * p * ((s + 1) * p + s) + 1;
}

function easeOutCubic(u: number): number {
  const p = 1 - u;
  return 1 - p * p * p;
}

/* ------------------------------------------------------------------ layer -- */

interface Popup {
  view: Text;
  variant: PopupVariant;
  active: boolean;
  delay: number;
  life: number;
  maxLife: number;
  originX: number;
  originY: number;
  rise: number;
  drift: number;
  spec: VariantSpec;
}

/** Reused by `#place` so placement never allocates. */
const placed = { x: 0, y: 0 };

export class ScorePopupLayer {
  /** Add this above the particle layer — numbers must never be lost inside a burst. */
  readonly view: Container;

  readonly #pool: Popup[] = [];
  readonly #styles = new Map<PopupVariant, TextStyle>();

  #reduced: boolean;
  #renderScale = 1;
  #autoScale = true;
  #destroyed = false;

  constructor(options: ScorePopupOptions = {}) {
    const capacity = Math.max(4, Math.floor(options.capacity ?? 28));
    this.#reduced = options.reducedMotion ?? false;

    this.view = new Container({ label: 'vfx-score-popups' });

    for (const variant of Object.keys(VARIANTS) as PopupVariant[]) {
      this.#styles.set(variant, this.#buildStyle(variant));
    }

    // Seed from the display so the very first popup is already sharp, before
    // `update()` has had a chance to read the layer's world transform.
    const dpr = typeof globalThis.devicePixelRatio === 'number' ? globalThis.devicePixelRatio : 1;
    this.#renderScale = this.#quantise(dpr);

    const fallback = VARIANTS.score;
    for (let i = 0; i < capacity; i++) {
      const view = new Text({
        text: '',
        style: this.#styles.get('score') as TextStyle,
        anchor: 0.5,
        resolution: this.#renderScale,
      });
      view.visible = false;
      this.view.addChild(view);
      this.#pool.push({
        view,
        variant: 'score',
        active: false,
        delay: 0,
        life: 0,
        maxLife: fallback.life,
        originX: 0,
        originY: 0,
        rise: 0,
        drift: 0,
        spec: fallback,
      });
    }
  }

  #buildStyle(variant: PopupVariant): TextStyle {
    const spec = VARIANTS[variant];
    const fontSize = CARD.height * spec.size;
    const strokeWidth = fontSize * 0.085;
    return new TextStyle({
      fontFamily: typeTokens.display,
      fontSize,
      letterSpacing: fontSize * TRACKING,
      // Pixi sizes the glyph canvas from the measured text only, so the shadow
      // has to be paid for explicitly or it gets clipped at the bounds.
      padding: Math.ceil(SHADOW_BLUR + SHADOW_DISTANCE + strokeWidth),
      fill: gradientFor(spec.colour),
      // A dark contour keeps the number legible when it crosses its own burst.
      stroke: {
        color: palette.ink,
        width: strokeWidth,
        join: 'round',
        cap: 'round',
      },
      dropShadow: {
        color: palette.ink,
        alpha: SHADOW.alphaBase,
        angle: shadowAngle(),
        blur: SHADOW_BLUR,
        distance: SHADOW_DISTANCE,
      },
      align: 'center',
    });
  }

  /* --------------------------------------------------------------- config -- */

  get reducedMotion(): boolean {
    return this.#reduced;
  }

  setReducedMotion(value: boolean): void {
    this.#reduced = value;
  }

  /**
   * Pin the rasterisation scale. Pass `renderer.resolution * stageScale` so the
   * glyphs are baked at true device pixels. Pass `null` to go back to deriving
   * it from the layer's own world transform, which is the default and needs no
   * cooperation from the stage.
   */
  setRenderScale(scale: number | null): void {
    if (scale === null) {
      this.#autoScale = true;
      return;
    }
    this.#autoScale = false;
    this.#renderScale = this.#quantise(scale);
  }

  /**
   * Quantised so a continuously resizing window cannot re-rasterise the font on
   * every frame, and capped so a very high-DPI display cannot blow up the text
   * atlas.
   */
  #quantise(scale: number): number {
    return clamp(Math.ceil(scale * 2) / 2, 1, 3);
  }

  get activeCount(): number {
    let n = 0;
    for (const popup of this.#pool) if (popup.active) n++;
    return n;
  }

  /* ----------------------------------------------------------------- show -- */

  /**
   * Show one popup. Returns nothing — placement is automatic, and the caller
   * should not need to know what else is already on screen.
   */
  show(request: PopupRequest): void {
    if (this.#destroyed || request.text.length === 0) return;

    const spec = VARIANTS[request.variant];
    const popup = this.#take();
    // Retire the slot before placing, or a recycled popup collides with itself.
    popup.active = false;
    // The rise is resolved before placing, because where a popup may start
    // depends on how far it is about to travel.
    const rise = CARD.height * spec.rise * (this.#reduced ? 0.55 : 1);
    const point = this.#place(request.x, request.y + (request.offsetY ?? 0), spec.sink ? 0 : rise);

    popup.active = true;
    popup.variant = request.variant;
    popup.spec = spec;
    popup.delay = Math.max(0, request.delay ?? 0);
    popup.life = 0;
    popup.maxLife = spec.life;
    popup.originX = point.x;
    popup.originY = point.y;
    popup.rise = rise;
    popup.drift = this.#reduced ? 0 : (Math.random() < 0.5 ? -1 : 1) * CARD.width * 0.05;

    const style = this.#styles.get(request.variant);
    if (style && popup.view.style !== style) popup.view.style = style;

    const resolution = this.#currentScale();
    if (popup.view.resolution !== resolution) popup.view.resolution = resolution;

    if (popup.view.text !== request.text) popup.view.text = request.text;

    popup.view.position.set(point.x, point.y);
    popup.view.scale.set(this.#reduced ? 1 : 0.4);
    popup.view.alpha = 0;
    popup.view.visible = popup.delay <= 0;
  }

  /**
   * Show a whole play's worth of numbers as one legible group: each entry sits
   * a step below the one before it, is nudged to the opposite side, and starts
   * a beat later, so a wild + 21 + 5-card + streak reads as a cascade rather
   * than four numbers fighting for the same pixels.
   */
  showStack(entries: readonly PopupEntry[], x: number, y: number, delay = 0): void {
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      this.show({
        text: entry.text,
        variant: entry.variant,
        x: x + (i % 2 === 0 ? -1 : 1) * (i === 0 ? 0 : STACK_NUDGE),
        y,
        offsetY: i * STACK_STEP,
        delay: delay + i * STACK_STAGGER,
      });
    }
  }

  /**
   * Find a free slot in the column. Anything already in flight near the target
   * pushes the newcomer further down and across; after a handful of attempts we
   * take what we have rather than walking off the table.
   */
  #place(x: number, y: number, rise: number): { x: number; y: number } {
    let px = x;
    let py = y;
    for (let attempt = 0; attempt < MAX_PLACE_ATTEMPTS; attempt++) {
      let clash = false;
      for (const popup of this.#pool) {
        if (!popup.active) continue;
        if (Math.abs(popup.originX - px) < COLLIDE_X && Math.abs(popup.originY - py) < COLLIDE_Y) {
          clash = true;
          break;
        }
      }
      if (!clash) break;
      py += STACK_STEP;
      px = x + (attempt % 2 === 0 ? STACK_NUDGE : -STACK_NUDGE);
    }
    placed.x = clamp(px, CARD.width * 0.5, DESIGN.width - CARD.width * 0.5);
    // Y is clamped for the position the popup will *end* at, not the one it
    // starts from. It rises by `spec.rise` over its life, so a popup spawned
    // from a pile near the top of a short design rect finishes above the rect
    // entirely and the number the player is trying to read is the part that
    // gets cut. X has been clamped here since the start; Y was not, and only
    // landscape is short enough for it to show.
    placed.y = clamp(py, POPUP_EDGE + Math.max(0, rise), DESIGN.height - POPUP_EDGE);
    return placed;
  }

  /** The least-recently-spawned free slot, or the oldest live popup if all are busy. */
  #take(): Popup {
    let oldest: Popup = this.#pool[0];
    let oldestProgress = -1;
    for (const popup of this.#pool) {
      if (!popup.active) return popup;
      const progress = popup.life / popup.maxLife;
      if (progress > oldestProgress) {
        oldestProgress = progress;
        oldest = popup;
      }
    }
    return oldest;
  }

  /* ----------------------------------------------------------------- tick -- */

  update(dt: number): void {
    if (this.#destroyed) return;
    const step = clamp(dt, 0, 1 / 15);
    if (step <= 0) return;

    if (this.#autoScale) {
      const world = this.view.worldTransform;
      const magnitude = Math.sqrt(world.a * world.a + world.b * world.b) || 1;
      const dpr = typeof globalThis.devicePixelRatio === 'number' ? globalThis.devicePixelRatio : 1;
      const next = this.#quantise(magnitude * dpr);
      if (next !== this.#renderScale) this.#renderScale = next;
    }

    for (const popup of this.#pool) {
      if (!popup.active) continue;

      if (popup.delay > 0) {
        popup.delay -= step;
        if (popup.delay > 0) continue;
        popup.view.visible = true;
      }

      popup.life += step;
      if (popup.life >= popup.maxLife) {
        popup.active = false;
        popup.view.visible = false;
        popup.view.alpha = 0;
        continue;
      }

      const spec = popup.spec;
      const t = popup.life / popup.maxLife;
      const travel = easeOutCubic(t);

      const direction = spec.sink ? 1 : -1;
      popup.view.position.set(
        popup.originX + popup.drift * travel,
        popup.originY + direction * popup.rise * travel,
      );

      let scale = 1;
      if (this.#reduced) {
        scale = 1 + (spec.endScale - 1) * travel * 0.4;
      } else {
        const entrance = clamp(popup.life / IN_TIME, 0, 1);
        const pop = 0.4 + 0.6 * backOut(entrance, spec.overshoot);
        scale = pop * (1 + (spec.endScale - 1) * t);
      }
      popup.view.scale.set(scale);

      let alpha = clamp(popup.life / FADE_IN_TIME, 0, 1);
      const outStart = popup.maxLife - OUT_TIME;
      if (popup.life > outStart) {
        alpha *= clamp(1 - (popup.life - outStart) / OUT_TIME, 0, 1);
      }
      popup.view.alpha = alpha;
    }
  }

  #currentScale(): number {
    return this.#renderScale;
  }

  /** Drop everything on screen immediately — used on undo, restart and teardown. */
  /**
   * Rasterise one popup of every variant, invisibly, at load.
   *
   * A popup's `Text` is not turned into a texture until it is drawn, and the
   * variants differ in size and colour, so each is its own upload. Left alone
   * they arrive one at a time on the first `+400`, the first `BUST` and the
   * first `PERFECT` — every one of them a moment the game has just made loud.
   */
  warm(): void {
    if (this.#destroyed) return;
    const variants = Object.keys(VARIANTS) as PopupVariant[];
    for (const variant of variants) {
      this.show({ text: '+000', variant, x: DESIGN.width / 2, y: DESIGN.height / 2 });
    }
    this.view.alpha = WARM_ALPHA;
  }

  /** Undo {@link warm}. */
  cool(): void {
    this.view.alpha = 1;
    this.clear();
  }

  clear(): void {
    for (const popup of this.#pool) {
      popup.active = false;
      popup.delay = 0;
      popup.life = 0;
      popup.view.visible = false;
      popup.view.alpha = 0;
    }
  }

  dispose(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.clear();
    this.view.removeChildren();
    // Styles are shared across the pool, so the views must not take them down.
    for (const popup of this.#pool) popup.view.destroy();
    this.#pool.length = 0;
    for (const style of this.#styles.values()) style.destroy();
    this.#styles.clear();
    this.view.destroy();
  }
}
